import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Snackbar,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import MuiAlert from "@mui/material/Alert";
import {
  getDoctorById,
  updateDoctor,
  getHospitalsAndHealthCenters,
} from "./../../ApiConfig.jsx";

import { MuiTelInput } from "mui-tel-input";
const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  field: yup.string().required("Field is required"),
  professionalId: yup.string().required("Professional Id is required"),
  hospitalId: yup.string().required("Hospital is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .matches(/^\+\d(\s*\d){5,13}$/, "Phone number is invalid")
    .required("Phone number is required"),
});

function EditDoctor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState("Inactive");
  const initialUserDetails = {
    name: "",
    field: "",
    professionalId: "",
    hospitalId: "",
    email: "",
    phone: "",
  };

  const [itemDetails, setItemDetails] = useState(initialUserDetails);
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hospitalData, setHospitalData] = useState([]);
  const getHospital = async () => {
    const response = await getHospitalsAndHealthCenters();
    setHospitalData(response);
  };

  useEffect(() => {
    getHospital();
  }, []);

  const fetchItemData = async (id) => {
    try {
      const data = await getDoctorById(id);
      if (data.success) {
        console.log(data.data);
        setStatus(data.data.status);
        setItemDetails({
          name: data.data.name,
          field: data.data.field,
          professionalId: data.data.professionalId,
          hospitalId: data.data.hospitalId,
          phone: data.data.phone,
          email: data.data.email,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchItemData(id);
  }, [id]);

  const validateField = (field, value) => {
    validationSchema
      .validateAt(field, { ...itemDetails, [field]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
      })
      .catch((error) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: error.message }));
      });
  };

  const handleFieldChange = (field, value) => {
    setItemDetails((prevUserDetails) => ({
      ...prevUserDetails,
      [field]: value,
    }));
    validateField(field, value);
  };

  const updateHandler = async (userDetails) => {
    const hasErrors = Object.values(errors).some(
      (errorMessage) => errorMessage !== ""
    );

    if (hasErrors) {
      console.log("Errors found. Cannot update doctor.");
      return;
    }

    console.log(userDetails);
    setIsLoading(true);
    const body = {
      ...userDetails,
      status,
    };
    try {
      const respondData = await updateDoctor(id, body);
      console.log(respondData);
      if (respondData.success) {
        setAlertSeverity("success");
        setAlertMessage("Doctor edited successfully");
        setTimeout(() => {
          navigate("/doctorManagement");
        }, 2500);
      }
    } catch (err) {
      console.log(err);
      setAlertSeverity("error");
      setAlertMessage(`Failed to edit doctor: ${err.message}`);
    } finally {
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title={`Edit doctor ${id}`} subtitle="" />
      <Box ml={"40px"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Name*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Field*
            </Typography>
            <FormControl fullWidth variant="filled">
              <Select
                value={itemDetails.field}
                onChange={(e) => handleFieldChange("field", e.target.value)}
                name="field"
                error={!!errors.field}>
                <MenuItem value="md">MD</MenuItem>
                <MenuItem value="assist_md">Assist MD</MenuItem>
              </Select>
              {errors.field && (
                <Typography variant="caption" color="error">
                  {errors.field}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Professional Id*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.professionalId}
              onChange={(e) =>
                handleFieldChange("professionalId", e.target.value)
              }
              error={!!errors.professionalId}
              helperText={errors.professionalId}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Hospital*
            </Typography>
            <FormControl fullWidth variant="filled">
              <Select
                value={itemDetails.hospitalId}
                onChange={(e) =>
                  handleFieldChange("hospitalId", e.target.value)
                }
                name="hospitalId"
                error={!!errors.hospitalId}>
                {hospitalData.map((data) => (
                  <MenuItem value={data.id}>{data.name}</MenuItem>
                ))}
              </Select>
              {errors.hospitalId && (
                <Typography variant="caption" color="error">
                  {errors.hospitalId}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Email*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Phone*
            </Typography>
            <MuiTelInput
              inputProps={{ maxLength: 16 }}
              type="text"
              fullWidth
              defaultCountry={"us"}
              variant="filled"
              value={itemDetails.phone}
              onChange={(value, country) => handleFieldChange("phone", value)}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#6870fa",
                color: "white",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "#3e4396",
                },
              }}
              onClick={() => updateHandler(itemDetails)}>
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <MuiAlert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          style={{ color: "white" }}>
          {alertSeverity === "success" ? "Success" : "Error"}
          {": "}
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default EditDoctor;
